import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Phone, Send } from "lucide-react";
import { FilterBar, inRange } from "@/components/admin/FilterBar";
import { fmtTs } from "@/components/Timeline";
import { RouteToTeam, EscBadge, useEscalations } from "@/components/admin/TeamRouting";

const STATUSES = ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"];
const TONE = { OPEN: "bg-amber-50 text-amber-700", IN_PROGRESS: "bg-sky-50 text-sky-700", RESOLVED: "bg-emerald-50 text-emerald-700", CLOSED: "bg-navy/5 text-navy/50" };

export default function AdminSupport() {
    const [rows, setRows] = useState(null);
    const [q, setQ] = useState("");
    const [st, setSt] = useState("");
    const [role, setRole] = useState("");
    const [dates, setDates] = useState({ from: "", to: "" });
    const load = () => api.get("/admin/support/tickets").then((r) => setRows(r.data)).catch(() => setRows([]));
    useEffect(() => { load(); }, []);
    const [esc, loadEsc] = useEscalations("ticket");
    const shown = (rows || []).filter((t) => (!st || t.status === st) && (!role || t.role === role) && inRange(t.created_at, dates)
        && (!q || `${t.ticket_id} ${t.name} ${t.phone} ${t.topic} ${t.booking_ref || ""}`.toLowerCase().includes(q.toLowerCase())));
    return (
        <div>
            <h1 className="font-heading text-2xl font-extrabold text-navy">Helpdesk</h1>
            <p className="text-sm text-navy/55">Customer & Professional tickets — reply, track and resolve.</p>
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {STATUSES.map((s) => (
                    <button key={s} data-testid={`ticket-kpi-${s.toLowerCase()}`} onClick={() => setSt(st === s ? "" : s)} className={`rounded-2xl border p-3 text-left ${st === s ? "border-orange bg-orange/5" : "border-navy/10 bg-white"}`}>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-navy/45">{s.replace("_", " ")}</p>
                        <p className="font-heading text-xl font-extrabold text-navy">{(rows || []).filter((t) => t.status === s).length}</p>
                    </button>
                ))}
            </div>
            <FilterBar testid="ticket-filter" q={q} setQ={setQ} placeholder="Search ticket, name, phone, topic…" dates={dates} setDates={setDates}
                selects={[{ key: "status", label: "All statuses", value: st, set: setSt, options: STATUSES },
                    { key: "role", label: "Customers & Pros", value: role, set: setRole, options: [{ value: "customer", label: "Customers" }, { value: "professional", label: "Professionals" }] }]}
                onClear={() => { setQ(""); setSt(""); setRole(""); setDates({ from: "", to: "" }); }} />
            {rows === null && <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
            <div className="mt-4 space-y-3">
                {shown.map((t) => <AdminTicket key={t.id} t={t} esc={esc[t.id]} onChanged={() => { load(); loadEsc(); }} />)}
                {rows && !shown.length && <div className="rounded-2xl border border-navy/10 bg-white py-12 text-center text-sm text-navy/40">No tickets match.</div>}
            </div>
        </div>
    );
}

function AdminTicket({ t, esc, onChanged }) {
    const [open, setOpen] = useState(false);
    const [reply, setReply] = useState("");
    const act = async (fn, msg) => {
        try { await fn(); toast.success(msg); setReply(""); onChanged(); }
        catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    };
    return (
        <div data-testid={`admin-ticket-${t.ticket_id}`} className="rounded-2xl border border-navy/10 bg-white">
            <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center gap-2 p-4 text-left">
                <div className="min-w-0 flex-1">
                    <p className="font-heading font-bold text-navy">{t.ticket_id} · {t.topic}</p>
                    <p className="text-xs text-navy/55"><b className="capitalize">{t.role}</b> · {t.name} · {t.phone}{t.booking_ref ? ` · ${t.booking_ref}` : ""} · {fmtTs(t.updated_at)}</p>
                </div>
                <EscBadge e={esc} />
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${TONE[t.status]}`}>{t.status.replace("_", " ")}</span>
            </button>
            {open && (
                <div className="space-y-2 border-t border-navy/10 p-4">
                    {t.messages.map((m, i) => (
                        <div key={i} className={`rounded-xl p-2.5 text-sm ${m.from === "admin" ? "ml-8 bg-teal/5" : "mr-8 bg-navy/[0.03]"}`}>
                            <p className="text-[11px] font-bold text-navy/55">{m.name} · {fmtTs(m.at)}</p>
                            <p className="whitespace-pre-wrap text-navy">{m.text}</p>
                        </div>
                    ))}
                    <div className="flex gap-2">
                        <input data-testid={`admin-ticket-reply-${t.ticket_id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply to user…" className="flex-1 rounded-full border border-navy/15 px-4 py-2 text-sm outline-none focus:border-orange" />
                        <button data-testid={`admin-ticket-send-${t.ticket_id}`} disabled={!reply.trim()} onClick={() => act(() => api.post(`/support/tickets/${t.id}/reply`, { message: reply }), "Reply sent")} className="rounded-full bg-navy p-2.5 text-white disabled:opacity-50"><Send size={15} /></button>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        {t.phone && <a href={`tel:${t.phone}`} className="flex items-center gap-1 rounded-full border border-navy/15 px-3 py-1.5 text-xs font-bold text-navy"><Phone size={13} /> Call</a>}
                        {t.booking_id && <Link to={`/admin/bookings/${t.booking_id}`} className="rounded-full border border-navy/15 px-3 py-1.5 text-xs font-bold text-navy">Open booking</Link>}
                        {STATUSES.filter((s) => s !== t.status).map((s) => (
                            <button key={s} data-testid={`admin-ticket-${s.toLowerCase()}-${t.ticket_id}`} onClick={() => act(() => api.patch(`/admin/support/tickets/${t.id}`, { status: s, note: reply || undefined }), `Marked ${s.toLowerCase()}`)} className="rounded-full bg-navy/5 px-3 py-1.5 text-xs font-bold text-navy/70 hover:bg-navy/10">Mark {s.replace("_", " ").toLowerCase()}</button>
                        ))}
                    </div>
                    <RouteToTeam kind="ticket" sourceId={t.id} esc={esc} onDone={onChanged} />
                </div>
            )}
        </div>
    );
}
